import type { MouseEvent } from "react";
import { FIELD_ACTION_WORKING, fieldActionName } from "../../constants/caseReviewLabels";
import type { FieldAgentAction } from "../../model/fieldActionContext";
import { CaseReviewIcon } from "./CaseReviewIcon";

interface FieldAgentActionButtonProps {
  actions: FieldAgentAction[];
  fieldLabel: string;
  /** Why the actions cannot be used right now; when set, the buttons say so and do nothing. */
  disabledReason?: string;
  /** True while Theragenie is working on an action this field started: the button says so and waits. */
  working?: boolean;
  onActivate: (actionId: string) => void;
}

/**
 * The catalog's actions on one field, one button each: "Write narrative for Final Narrative with
 * Theragenie". A disabled button stays in the tab order and names its reason, because a reviewer
 * in read mode should learn that pressing Edit unlocks it rather than find nothing there.
 */
export function FieldAgentActionButton({ actions, fieldLabel, disabledReason, working = false, onActivate }: FieldAgentActionButtonProps) {
  const disabled = disabledReason !== undefined || working;
  // A native button already activates on Enter and Space; `aria-disabled` keeps it in the tab order.
  const activate = (event: MouseEvent, actionId: string) => {
    event.stopPropagation();
    if (disabled) return;
    onActivate(actionId);
  };
  return <span className="cr-field-actions">
    {actions.map((action) => {
      const reasonId = `field-action-${action.id}-reason`;
      return <span key={action.id} className="cr-field-action-slot">
        <button type="button" className="cr-field-action" data-working={working || undefined} aria-busy={working || undefined}
          aria-disabled={disabled} aria-label={fieldActionName(action.label, fieldLabel)}
          aria-describedby={disabledReason !== undefined ? reasonId : undefined}
          title={working ? FIELD_ACTION_WORKING : disabledReason ?? fieldActionName(action.label, fieldLabel)}
          onClick={(event) => activate(event, action.id)}>
          {working
            ? <><span className="cr-field-action-spinner" aria-hidden="true" />{FIELD_ACTION_WORKING}</>
            : <><CaseReviewIcon name="spark" size={12} strokeWidth={2.2} />{action.label}</>}
        </button>
        {disabledReason !== undefined && <span id={reasonId} className="sr-only">{disabledReason}</span>}
      </span>;
    })}
  </span>;
}
